import { Calendar, Clock, User } from "lucide-react";
import type LocalDateTime from "~/lib/localdatetime";
import { getDateLocaleByLocale, useLanguageStore } from "~/stores/language-store";

type SummaryCardProps = {
    storeName: string;
    storeAddress: string;
    storeImage?: string;
    startTime?: LocalDateTime;
    endTime?: LocalDateTime;
    customerName: string;
};

const SummaryCard = ({ storeName, storeAddress, storeImage, startTime, endTime, customerName }: SummaryCardProps) => {
    const { language } = useLanguageStore();

    const dateStr = startTime?.format("EEEE, d. LLLL yyyy", { locale: getDateLocaleByLocale(language) });
    const timeOfDayStr = startTime && endTime
        ? startTime.format("HH:mm", { locale: getDateLocaleByLocale(language) }) + " - " + endTime.format("HH:mm", { locale: getDateLocaleByLocale(language) }) + " Uhr"
        : null;

    const chips = [
        { icon: Calendar, text: dateStr },
        { icon: Clock, text: timeOfDayStr },
        { icon: User, text: customerName.trim() },
    ].filter((chip): chip is { icon: typeof Calendar; text: string } => !!chip.text);

    return (
        <div className='-mt-2 pt-8 pb-6 px-6 bg-white rounded-b-xl shadow-[0_4px_12px_-6px_rgba(86,28,44,0.25)]'>
            <div className='max-w-2xl mx-auto flex flex-col gap-4'>
                <div className='flex flex-row items-center gap-4'>
                    {storeImage && (
                        <img
                            className='w-14 h-14 shrink-0 object-cover rounded-md'
                            src={storeImage}
                            alt={storeName}
                        />
                    )}
                    <div className='flex flex-col gap-1 min-w-0'>
                        <span className='text-sm font-semibold text-burgundy'>
                            Kalia Lab {storeName}
                        </span>
                        <span className='text-sm font-light text-burgundy'>
                            {storeAddress}
                        </span>
                    </div>
                </div>

                {chips.length > 0 && (
                    <div className='flex flex-row flex-wrap gap-2'>
                        {chips.map((chip, index) => {
                            const Icon = chip.icon;
                            return (
                                <div key={index} className='inline-flex items-center gap-1.5 rounded-full bg-burgundy/10 px-3 py-1.5'>
                                    <Icon className='w-4 h-4 text-burgundy shrink-0' strokeWidth={2.5} />
                                    <span className='text-xs font-light text-burgundy'>{chip.text}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SummaryCard;
